/**
 * HelpPage — in-app pointer to operator docs.
 *
 * The HOWTOs live in the repo (api-shim/HOWTO.md, frontend/HOWTO.md) and
 * are not served by the SPA. This page surfaces the headline workflows
 * + the canonical operator answers so new users can self-serve before
 * tracking down a NetEng admin. Keep it short — long content drifts.
 */
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  KeyRound,
  ShieldCheck,
  Activity,
  Bot,
  LifeBuoy,
  Mail,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardSub, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Section {
  title: string;
  icon: LucideIcon;
  body: React.ReactNode;
}

const SECTIONS: Section[] = [
  {
    title: "Getting started",
    icon: BookOpen,
    body: (
      <ul className="list-disc space-y-1 pl-5 text-sm text-fg-dim">
        <li>
          Add your Meraki key on{" "}
          <Link to="/meraki/keys" className="text-violet underline">
            Meraki → Keys
          </Link>
          . Add your Aruba Central bundle on{" "}
          <Link to="/aruba/keys" className="text-violet underline">
            Aruba → Keys
          </Link>
          .
        </li>
        <li>
          The{" "}
          <Link to="/" className="text-violet underline">
            Overview
          </Link>{" "}
          page surfaces health, recent compliance drift, and the workflow
          queue.
        </li>
        <li>
          Vendor pages always read live state. Workflows that mutate use
          a propose-then-confirm pattern.
        </li>
      </ul>
    ),
  },
  {
    title: "Credentials & keys",
    icon: KeyRound,
    body: (
      <p className="text-sm text-fg-dim">
        Each user holds their own Meraki + Aruba credentials, scoped to
        their tenant view. Inactive keys can be re-activated from the
        Keys page; deleting a key revokes all stored secret material.
        Failed refresh on Aruba? Paste a fresh bundle — refresh tokens
        are single-use and the previous one is consumed.
      </p>
    ),
  },
  {
    title: "Compliance & drift",
    icon: ShieldCheck,
    body: (
      <p className="text-sm text-fg-dim">
        <Link to="/compliance" className="text-violet underline">
          Compliance
        </Link>{" "}
        scans a network against a curated preset, flags drift, and (for
        supported presets) offers a one-click remediate that targets only
        what's out of policy — your other settings are untouched.
        Allowed remediation targets are env-allowlisted; talk to a NetEng
        admin to extend scope.
      </p>
    ),
  },
  {
    title: "Monitor",
    icon: Activity,
    body: (
      <p className="text-sm text-fg-dim">
        <Link to="/monitor" className="text-violet underline">
          Monitor
        </Link>{" "}
        polls alert sources every few minutes. Acks persist server-side
        so cross-team handoffs survive a browser refresh. The bell in
        the top bar shows your unacked count.
      </p>
    ),
  },
  {
    title: "AI agent",
    icon: Bot,
    body: (
      <p className="text-sm text-fg-dim">
        The agent runs propose-then-confirm — it never mutates without
        an explicit click. Writes are restricted by per-vendor
        allowlists AND your role (admins see admin-only tools like
        backup/restore and audit search). If the agent looks disabled,
        check that <code className="text-fg">agentEnabled</code> is on
        for your account on{" "}
        <Link to="/users" className="text-violet underline">
          Users
        </Link>
        .
      </p>
    ),
  },
  {
    title: "Reporting issues",
    icon: LifeBuoy,
    body: (
      <p className="text-sm text-fg-dim">
        Hit something unexpected? Capture the page URL, the time, and
        the page you were on, then email the NetEng team. Audit log
        rows are searchable from{" "}
        <Link to="/audit" className="text-violet underline">
          Audit log
        </Link>{" "}
        — useful when corroborating "what did the agent actually do".
      </p>
    ),
  },
];

export function HelpPage() {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
          Help
        </h1>
        <p className="text-sm text-fg-mute">
          The basics for getting work done in Opus. NetEng owns the
          deeper operator docs (HOWTO.md in the repo) — ping them if
          something here is unclear or out of date.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {SECTIONS.map(({ title, icon: Icon, body }) => (
          <Card key={title}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-aurora-soft text-violet">
                  <Icon size={16} />
                </div>
                <div>
                  <CardTitle>{title}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>{body}</CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Still stuck?</CardTitle>
          <CardSub>
            Reach the NetEng team if you need access changes, a key
            reset, or you're seeing something this page doesn't cover.
          </CardSub>
        </CardHeader>
        <CardContent>
          <Button asChild size="sm" variant="ghost">
            <a href="mailto:neteng@example.com">
              <Mail size={14} />
              Email NetEng
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
